/**
 * MultiInputHandle
 * A single ReactFlow target handle that accepts up to N simultaneous
 * connections — avoids cluttering nodes with many individual input ports
 * when the node logically takes a "bag of N inputs of the same type".
 *
 * Usage:
 *   <MultiInputHandle id="multiInput" nodeId={id} limit={14} label="Imágenes" />
 *
 * The component:
 *   • renders a visually distinct stacked-circles icon next to the handle
 *   • shows a live "N/limit" badge on hover
 *   • refuses new connections once N >= limit (isConnectable=false)
 */
import { memo, useMemo } from 'react';
import { Handle, Position, useStore } from 'reactflow';

const MultiInputHandle = memo(({
  id = 'multiInput',
  nodeId,
  limit = 10,
  label = 'Múltiple',
  top,
  accent = '#10b981',
  dataTestId,
}) => {
  const edges = useStore((s) => s.edges);

  const connectionCount = useMemo(
    () => edges.filter((e) => e.target === nodeId && e.targetHandle === id).length,
    [edges, nodeId, id]
  );

  const isAtLimit = connectionCount >= limit;

  // Only allow connecting while count < limit
  const isConnectable = () => !isAtLimit;

  return (
    <div
      className={`multi-input-wrapper ${isAtLimit ? 'is-full' : ''}`}
      style={{ top: top ?? '50%', '--multi-accent': accent }}
      data-testid={dataTestId || `multi-input-${nodeId}`}
    >
      <Handle
        type="target"
        position={Position.Left}
        id={id}
        isConnectable={isConnectable}
        className="node-handle multi-input-handle"
      />
      <svg
        className="multi-input-icon"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        aria-hidden="true"
      >
        {/* three stacked circles = "multiple" affordance */}
        <circle cx="3" cy="3"  r="1.4" />
        <circle cx="9" cy="3"  r="1.4" />
        <circle cx="3" cy="9"  r="1.4" />
        <circle cx="9" cy="9"  r="1.4" />
      </svg>
      <span className="multi-input-badge" data-testid={`${dataTestId || 'multi-input'}-count`}>
        {connectionCount}/{limit}
      </span>
      <span className="multi-input-label">{label}</span>
      <div
        className="multi-input-tooltip"
        role="tooltip"
        data-testid={`${dataTestId || 'multi-input'}-tooltip`}
      >
        {isAtLimit
          ? `Límite alcanzado (${limit}/${limit})`
          : `Conectá hasta ${limit} ${label.toLowerCase()} aquí`}
      </div>
    </div>
  );
});

MultiInputHandle.displayName = 'MultiInputHandle';
export default MultiInputHandle;
